import React, {
  RefObject, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FloorControl } from 'src/components/FloorControl';
import { getFloorItemType, getIsRerender } from 'src/store/view-2d/selectors';
import { View2dType } from 'src/typings/view-2d';
import { Actions } from 'src/store/app/actions';
import { Actions as FloorsActions } from 'src/store/floors/actions';
import { StatusList } from 'src/components/CrosstableContainer/components/StatusList';
import { EFloorType } from 'src/store/view-2d/reducer';
import { ZoomTable } from 'src/components/CrosstableContent/components/ZoomTable';
import { useIsMobile } from 'src/hooks';
import { getCrmData, getIsOpenAppartSidebar, getSelectedAppartment } from 'src/store/app/selectors';
import { getBitrixDeal } from 'src/store/crosstable/selectors';
import { CRMNames } from 'src/typings/app';
import useWindowDimensions from 'src/hooks/use-window-dimensions';
import { useStyles } from './styles';
import { SectionsList } from './components';
import { ICrossTableContent } from './types';
import {
  useCrossTableData,
} from './hooks';
import { useGetTypeView2d } from './components/SectionsList/useGetTypeView2d';

export const CrossTableContent: React.FC<ICrossTableContent> = React.memo(({
  view2dData,
  floorList,
}) => {
  const floorItemType = useSelector(getFloorItemType);
  const isRerender = useSelector(getIsRerender);
  const crossTableContentRef: RefObject<HTMLDivElement> = useRef(null);
  const dispatch = useDispatch();
  const selectedAppartment = useSelector(getSelectedAppartment);
  const isOpenAppartSidebar = useSelector(getIsOpenAppartSidebar);
  const crmData = useSelector(getCrmData);
  const bitrixDeal = useSelector(getBitrixDeal);
  const crmDataRef = useRef(crmData);
  crmDataRef.current = crmData;
  const styles = useStyles({});
  const { windowDimensions } = useWindowDimensions();
  const [zoomTableValue, setZoomTableValue] = useState<number | undefined>(undefined);
  // One auto-center per grid appearance — see the auto-center effect below.
  const centeredOnceRef = useRef(false);

  useEffect(() => {
    return () => {
      // In the bitrix widget the focused unit must survive view switches
      // (Dashboard <-> Floors/3D): keeping the selection lets the auto-center
      // effect below re-center it when the grid comes back.
      if (crmDataRef.current?.crm === CRMNames.bitrix) return;
      dispatch(Actions.setIsOpenAppartSidebar(false));
      dispatch(FloorsActions.setSelectedApartmentId(undefined));
    };
  }, []);

  // Auto-center the selected placement in the grid. On the Bitrix widget boot the
  // deal's unit is selected before the grid finishes rendering (and may be
  // virtualized off-screen to a skeleton), so we poll for the marked cell, find
  // its real scroll parent, and scroll ONLY that (not the page/iframe) to center
  // the cell — and only when it isn't already fully visible (no jump on clicks).
  useEffect(() => {
    // Two centering targets: the sidebar-selected unit, or — with the sidebar
    // closed in CRM deal mode — the current deal's unit (black outline). The
    // deal cell keeps centering working after the user closes the sidebar and
    // after Floors/3D -> Dashboard switches (this component remounts, the
    // effect re-runs).
    // Center ONLY when the grid appears (mount / re-render after link-unlink).
    // Store updates (deal refetch, sidebar toggles, cell clicks) re-run this
    // effect too — re-centering then would yank the grid away from wherever
    // the user has scrolled it.
    if (isRerender) {
      centeredOnceRef.current = false;
      return undefined;
    }
    if (centeredOnceRef.current) return undefined;
    const isDealMode = crmData?.crm === CRMNames.bitrix && !!bitrixDeal?.placement;
    if (!(isOpenAppartSidebar && selectedAppartment) && !isDealMode) return undefined;
    let cancelled = false;
    let tries = 0;
    const scrollParent = (el: Element): HTMLElement | null => {
      let node = el.parentElement;
      while (node) {
        const s = window.getComputedStyle(node);
        const scrollX = /(auto|scroll)/.test(s.overflowX) && node.scrollWidth > node.clientWidth;
        const scrollY = /(auto|scroll)/.test(s.overflowY) && node.scrollHeight > node.clientHeight;
        if (scrollX || scrollY) return node;
        node = node.parentElement;
      }
      return null;
    };
    const center = (container: HTMLElement, cell: Element) => {
      const c = container.getBoundingClientRect();
      const e = cell.getBoundingClientRect();
      // The zoom strip overlays the bottom of the grid and the apartment
      // sidebar can overlay its right side — those bands are NOT usable
      // viewport: a cell "visible" by the container rect still hides behind
      // them. Shrink the effective box by the overlaps.
      let bottomInset = 0;
      const zoomEl = (crossTableContentRef.current || document).querySelector('[data-grid-zoom]');
      if (zoomEl) {
        const z = zoomEl.getBoundingClientRect();
        if (z.height > 0 && z.top < c.bottom && z.bottom > c.top) {
          bottomInset = Math.max(0, c.bottom - z.top);
        }
      }
      let rightInset = 0;
      const sidebarEl = document.querySelector('[data-apartment-sidebar]');
      if (sidebarEl) {
        const s = sidebarEl.getBoundingClientRect();
        if (s.width > 0 && s.left < c.right && s.right > c.left
          && s.top < c.bottom && s.bottom > c.top) {
          rightInset = Math.max(0, c.right - s.left);
        }
      }
      const visibleBottom = c.bottom - bottomInset;
      const visibleRight = c.right - rightInset;
      const fullyVisible = e.left >= c.left && e.right <= visibleRight
        && e.top >= c.top && e.bottom <= visibleBottom;
      if (fullyVisible) return;
      const dx = (e.left - c.left) - ((container.clientWidth - rightInset) - e.width) / 2;
      const dy = (e.top - c.top) - ((container.clientHeight - bottomInset) - e.height) / 2;
      container.scrollTo({
        left: container.scrollLeft + dx,
        top: container.scrollTop + dy,
        behavior: 'smooth',
      });
    };
    const tick = () => {
      if (cancelled) return;
      const root = crossTableContentRef.current || document;
      const cell = root.querySelector('[data-selected-cell="true"]')
        || root.querySelector('[data-deal-cell="true"]');
      if (cell) {
        centeredOnceRef.current = true;
        const container = scrollParent(cell);
        if (container) {
          center(container, cell);
          // The sidebar opens with a 400ms width animation: the first pass can
          // measure the pre-animation layout, count the cell as visible and
          // skip — while the settled layout hides it. Re-verify after the
          // animation and the smooth scroll are done (center() no-ops when the
          // cell is already fully visible, so no extra jumps).
          [600, 1400].forEach((delay) => {
            setTimeout(() => {
              if (!cancelled) center(container, cell);
            }, delay);
          });
        }
        return;
      }
      tries += 1;
      // ~6s budget: the grid may still be loading get-filter-data on boot.
      if (tries < 120) setTimeout(tick, 50);
    };
    setTimeout(tick, 50);
    return () => {
      cancelled = true;
    };
    // isRerender in deps: link/unlink dispatches setIsRerender and remounts the
    // grid (losing scroll position) — when it flips back to false the effect
    // re-runs and re-centers the currently selected unit. bitrixDeal in deps:
    // on boot the deal arrives AFTER the first render — without it the deal-cell
    // fallback would never fire. The "already fully visible" check above keeps
    // unrelated updates from causing jumps.
  }, [selectedAppartment, isOpenAppartSidebar, isRerender, bitrixDeal]);

  useEffect(() => {
    let value = 50;
    if (windowDimensions && windowDimensions.width >= 1920) {
      value = 60;
    }
    if (windowDimensions && windowDimensions.width >= 3000) {
      value = 70;
    }
    setZoomTableValue(value);
  }, []);

  const { houseKey } = useCrossTableData(view2dData);
  const { getTypeView2d } = useGetTypeView2d();
  const { isMobileScreen } = useIsMobile();
  const hideZoom = useMemo<boolean>(() => {
    if (isMobileScreen) {
      return isOpenAppartSidebar;
    }
    return false;
  }, [isMobileScreen, isOpenAppartSidebar]);
  return (
    <div
      id="crossTableContent"
      ref={crossTableContentRef}
      className={`${getTypeView2d === View2dType.area ? 'appArea' : 'appRooms'}`}
    >
      <StatusList />
      <div className="crossTableContentInner">
        <FloorControl
          floorList={floorList}
          floorItemType={floorItemType}
        />
        <div className={styles.sections}>
          {houseKey.map((house) => {
            if (view2dData?.[house] && !isRerender) {
              return (
                <SectionsList
                  key={house}
                  floorList={floorList}
                  houseName={house}
                  houseKeys={houseKey}
                  houseData={view2dData[house]}
                />
              );
            }

            return null;
          })}
        </div>
      </div>
      {floorItemType === EFloorType.mini && (
        <div data-grid-zoom className={`${styles.zoom} ${hideZoom ? 'hidden' : ''}`}>
          <ZoomTable setCurrentValue={zoomTableValue} />
        </div>
      )}
    </div>
  );
});
